/**
 * Spend Dashboard — prebuilt, data-linked spend analytics.
 *
 * Renders the seeded "Barratt Spend Dashboard" report (see backend
 * `setup_barratt_spend_dashboard`): headline KPI tiles computed from the
 * monthly invoice series, then the four charts. Charts are view-scope aware —
 * a group admin sees the whole group's money, a division user their own.
 */
import { useEffect, useMemo, useState } from 'react';
import { reportingAPI } from '../../lib/api/reporting';
import { useView } from '../../contexts/ViewContext';
import { coreAPI } from '../../lib/api';
import { type DashboardIndicator } from '../../lib/api/barratt';
import {
  useIndicatorData,
  type AdhocIndicatorFilter,
} from '../report/charts/useIndicatorData';
import SearchableSelect from '../../components/ui/SearchableSelect';
import RankedBarList from './RankedBarList';
import MonthDrilldown, { type DrillMonth } from './MonthDrilldown';
import usePartDescriptions from './usePartDescriptions';
import MonthlySpendChart from './MonthlySpendChart';
import { commodityLabel } from './commodityNames';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  AlertCircle,
  Banknote,
  Building2,
  CalendarRange,
  Filter,
  TrendingUp,
  X,
} from 'lucide-react';

const DASHBOARD_NAME = 'Barratt Spend Dashboard';

const CHART_NAMES = {
  monthly: 'Monthly Spend (Invoice £)',
  division: 'Spend by Division',
  suppliers: 'Top 10 Suppliers by Spend',
  parts: 'Top 10 Parts by Spend',
  commodity: 'Spend by Commodity',
  lines: 'Spend Lines',
} as const;

/** £1,234,567 → "£1.23M" style compact money. */
function money(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `£${(value / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `£${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `£${(value / 1_000).toFixed(0)}K`;
  return `£${value.toFixed(0)}`;
}

function monthLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

interface StatTileProps {
  label: string;
  value: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
}

function StatTile({ label, value, hint, icon: Icon }: StatTileProps) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {label}
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
            {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
          </div>
          <div className="rounded-md bg-primary/10 p-2">
            <Icon className="h-4 w-4 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ChartCard({ title, subtitle, children }: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

interface SpendDataProps {
  /** Rendered inside the main Dashboard tab strip — the page header is redundant there. */
  embedded?: boolean;
}

export default function SpendData({ embedded = false }: SpendDataProps) {
  const [indicators, setIndicators] = useState<DashboardIndicator[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drillMonth, setDrillMonth] = useState<DrillMonth | null>(null);

  // "View Data For" scope. Sent explicitly with every indicator request so
  // switching group / division / site re-fetches the right slice at once —
  // the resolver honours these params ahead of the persisted view.
  const { getViewQueryParams } = useView();
  const scopeParams = useMemo(() => getViewQueryParams(), [getViewQueryParams]);

  // Filter state — same interaction model as the Scheduled dashboard: the
  // supplier is a searchable select, the part input auto-commits 400ms after
  // the user stops typing (Enter commits immediately, Esc clears).
  const [supplierName, setSupplierName] = useState<string>('');
  const [suppliers, setSuppliers] = useState<{ id: number; name: string }[]>([]);
  const [partInput, setPartInput] = useState<string>('');
  const [partCommitted, setPartCommitted] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => setPartCommitted(partInput.trim()), 400);
    return () => clearTimeout(t);
  }, [partInput]);

  // Portal-aware supplier list (customer portal → suppliers they trade with).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await coreAPI.getSupplierSitesForFilter();
        if (cancelled) return;
        const raw = resp.data as unknown;
        const results: Array<{ id: number; name: string }> =
          Array.isArray(raw) ? (raw as Array<{ id: number; name: string }>)
          : Array.isArray((raw as { results?: unknown }).results)
            ? (raw as { results: Array<{ id: number; name: string }> }).results
            : [];
        setSuppliers(
          results
            .map((c) => ({ id: c.id, name: c.name || `Supplier ${c.id}` }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      } catch { /* dropdown stays empty */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Ad-hoc filters the data endpoint appends to each indicator's own config.
  // Invoice Lines (dsid 708): field 1 = Supplier Name, field 7 = Product Code.
  const adhocFilters = useMemo<AdhocIndicatorFilter[]>(() => {
    const out: AdhocIndicatorFilter[] = [];
    if (supplierName) {
      out.push({ field_dsid: 708, field_id: 1, operator: 'is', value: supplierName });
    }
    if (partCommitted) {
      out.push({ field_dsid: 708, field_id: 7, operator: 'contains', value: partCommitted });
    }
    return out;
  }, [supplierName, partCommitted]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dashRes = await reportingAPI.getDashboards({ page_size: 100 });
        const dash = (dashRes.data.results || []).find(
          (d) => d.name === DASHBOARD_NAME
        );
        if (!dash) {
          if (!cancelled) setError('The spend dashboard has not been set up yet.');
          return;
        }
        const indRes = await reportingAPI.getIndicators({
          dashboard: dash.id, page_size: 50,
        });
        if (!cancelled) setIndicators(indRes.data.results || []);
      } catch (err: unknown) {
        console.error('Failed to load spend dashboard:', err);
        if (!cancelled) setError('Failed to load the spend dashboard. Try refreshing.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const byName = useMemo(() => {
    const map: Record<string, DashboardIndicator> = {};
    (indicators || []).forEach((i) => { map[i.name] = i; });
    return map;
  }, [indicators]);

  const monthlyId = byName[CHART_NAMES.monthly]?.id;
  const divisionId = byName[CHART_NAMES.division]?.id;
  const suppliersId = byName[CHART_NAMES.suppliers]?.id;
  const partsId = byName[CHART_NAMES.parts]?.id;
  const commodityId = byName[CHART_NAMES.commodity]?.id;
  const linesId = byName[CHART_NAMES.lines]?.id;   // drill-down feed, no card

  // Fetch every series at page level so the ad-hoc filters apply to all of
  // them; payloads reach the chart components via IndicatorDataContext
  // (no double-fetch).
  const monthly = useIndicatorData(monthlyId, adhocFilters, scopeParams);
  const division = useIndicatorData(divisionId, adhocFilters, scopeParams);
  const topSuppliers = useIndicatorData(suppliersId, adhocFilters, scopeParams);
  const topParts = useIndicatorData(partsId, adhocFilters, scopeParams);
  const commodity = useIndicatorData(commodityId, adhocFilters, scopeParams);

  // Commodity families are legacy mnemonics (TIMB, INDR, ...) — decode the
  // unambiguous ones; the rest show their raw code.
  const commodityRows = useMemo(
    () => commodity.data?.data?.map((r) => {
      const label = commodityLabel(r.name);
      return {
        name: label,
        sub: label === r.name ? undefined : r.name,
        value: r.value,
      };
    }),
    [commodity.data],
  );

  // The parts indicator aggregates by part NUMBER; people read descriptions.
  // Look the shown numbers up in the catalog and lead with the description.
  const partNumbers = useMemo(
    () => (topParts.data?.data || []).map((r) => r.name),
    [topParts.data],
  );
  const partDescs = usePartDescriptions(partNumbers);
  const partsRows = useMemo(
    () => topParts.data?.data?.map((r) => ({
      name: partDescs[r.name] || r.name,
      sub: partDescs[r.name] ? r.name : undefined,
      value: r.value,
    })),
    [topParts.data, partDescs],
  );

  const stats = useMemo(() => {
    // Every tile reads the ROLLING YEAR — the last 12 months of invoice data,
    // matching the charts below. Full history lives behind the chart's
    // "All history" toggle only.
    const rows = (monthly.data?.data || []).slice(-12);
    if (!rows.length) return null;
    const last12 = rows.reduce((s, r) => s + (r.value || 0), 0);
    const monthlyAvg = last12 / rows.length;
    const peak = rows.reduce((a, b) => ((b.value || 0) > (a.value || 0) ? b : a));
    // "Other" is the resolver's residual bucket, not a division — counting it
    // put a division on the tile that does not exist.
    const divisions = (division.data?.data || []).filter((row) => row.name !== 'Other').length;
    return { last12, monthlyAvg, peak, divisions };
  }, [monthly.data, division.data]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mb-3" />
        <p>{error}</p>
      </div>
    );
  }

  const loading = indicators === null || (monthlyId !== undefined && !monthly.data);

  return (
    <div className="space-y-6">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-foreground">Spend Dashboard</h1>
          <p className="text-muted-foreground">
            Invoiced spend across the group — from the invoice ledger, scoped to your current view
          </p>
        </div>
      )}

      {/* Filter bar — same shape as the Scheduled dashboard's */}
      <div
        className="rounded-lg border border-slate-200 bg-white px-4 py-3"
        role="group"
        aria-label="Spend dashboard filters"
      >
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <Filter className="h-3.5 w-3.5" />
            <span>Filters</span>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="spend-supplier" className="text-xs text-slate-500">Supplier</label>
            <div className="w-96">
              <SearchableSelect
                options={suppliers.map((s) => ({ value: s.name, label: s.name }))}
                value={supplierName}
                onChange={setSupplierName}
                placeholder="All suppliers"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="spend-part" className="text-xs text-slate-500">Part</label>
            <input
              id="spend-part"
              type="text"
              value={partInput}
              onChange={(e) => setPartInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') setPartCommitted(partInput.trim());
                if (e.key === 'Escape') { setPartInput(''); setPartCommitted(''); }
              }}
              placeholder="type part # — applies as you type"
              title="Auto-applies after you stop typing. Esc to clear."
              className="h-8 w-56 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300"
            />
          </div>

          {(supplierName || partCommitted) && (
            <button
              onClick={() => {
                setSupplierName('');
                setPartInput('');
                setPartCommitted('');
              }}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 hover:border-red-400"
            >
              <X className="h-3.5 w-3.5" /> Clear filters
            </button>
          )}
        </div>

        {(supplierName || partCommitted) && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <span className="text-slate-600">
              Filtered by {[
                supplierName ? `Supplier: ${supplierName}` : '',
                partCommitted ? `Part: ${partCommitted}` : '',
              ].filter(Boolean).join(' · ')} — tiles and every chart reflect the filter
            </span>
          </div>
        )}
      </div>

      {/* KPI tiles */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : stats && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatTile label="Invoiced Spend" value={money(stats.last12)}
                    hint="Last 12 months" icon={Banknote} />
          <StatTile label="Monthly Average" value={money(stats.monthlyAvg)}
                    hint="Last 12 months" icon={CalendarRange} />
          <StatTile label="Peak Month" value={money(stats.peak.value || 0)}
                    hint={`${monthLabel(stats.peak.name)} — last 12 months`} icon={TrendingUp} />
          <StatTile label="Divisions With Spend" value={String(stats.divisions)}
                    hint="Last 12 months" icon={Building2} />
        </div>
      )}

      {/* Charts — ranked lists carry one accent colour each; long names get
          the full row width instead of angled, truncated axis labels. */}
      {monthlyId && (
        <ChartCard title="Monthly Spend"
                   subtitle="Invoice value by month posted — click a month to drill down">
          <MonthlySpendChart rows={monthly.data?.data || []}
                             formatValue={money} onBarClick={setDrillMonth} />
        </ChartCard>
      )}
      <MonthDrilldown
        month={drillMonth}
        onClose={() => setDrillMonth(null)}
        linesId={linesId}
        baseFilters={adhocFilters}
        scopeParams={scopeParams}
        formatValue={money}
      />
      <div className="grid gap-6 lg:grid-cols-2">
        {divisionId && (
          <ChartCard title="Spend by Division"
                     subtitle="Share of invoiced spend — last 12 months">
            <RankedBarList rows={division.data?.data} color="#0f766e"
                           formatValue={money} maxRows={13} skeletonRows={13} />
          </ChartCard>
        )}
        {suppliersId && (
          <ChartCard title="Top 10 Suppliers"
                     subtitle="Largest suppliers by invoiced spend — last 12 months">
            <RankedBarList rows={topSuppliers.data?.data} color="#3b82f6"
                           formatValue={money} maxRows={10} skeletonRows={10} />
          </ChartCard>
        )}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        {commodityId && (
          <ChartCard title="Spend by Commodity"
                     subtitle="Largest commodity families by invoiced spend — last 12 months">
            <RankedBarList rows={commodityRows} color="#b45309"
                           formatValue={money} maxRows={12} skeletonRows={12} />
          </ChartCard>
        )}
        {partsId && (
          <ChartCard title="Top 10 Parts"
                     subtitle="Largest products by invoiced spend — last 12 months">
            <RankedBarList rows={partsRows} color="#7c3aed"
                           formatValue={money} maxRows={10} skeletonRows={10} />
          </ChartCard>
        )}
      </div>
    </div>
  );
}
